import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import SEO, { articleSchema } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(()=>{});
    api.get("/blog").then(r => setRelated(r.data.filter(p => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (!post) return <div className="pt-40 text-center text-white/60">Loading...</div>;

  return (
    <div data-testid="blog-detail" className="pt-32">
      <SEO title={post.seo_title || post.title} description={post.seo_description || post.excerpt}
           path={`/blog/${post.slug}`} image={post.cover_image} type="article"
           jsonLd={articleSchema(post)} />

      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">← Back to Journal</Link>
        <div className="eyebrow mt-8">{post.category}</div>
        <h1 className="font-serif text-4xl md:text-6xl font-light mt-4 tracking-tight leading-tight">{post.title}</h1>
        <div className="text-sm text-white/50 mt-4">By {post.author} · {new Date(post.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</div>
      </article>

      <div className="max-w-5xl mx-auto px-6">
        <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
      </div>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="font-serif text-2xl leading-relaxed text-white/85 italic mb-10 border-l-2 border-[#D4AF37] pl-6">
          {post.excerpt}
        </div>

        <div className="prose prose-invert prose-lg max-w-none
                        prose-headings:font-serif prose-headings:font-light prose-headings:tracking-tight
                        prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-5
                        prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
                        prose-p:text-white/75 prose-p:leading-relaxed
                        prose-li:text-white/75 prose-li:leading-relaxed
                        prose-strong:text-white prose-strong:font-semibold
                        prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline
                        prose-ul:my-4 prose-ol:my-4
                        prose-blockquote:border-l-[#D4AF37] prose-blockquote:text-white/70 prose-blockquote:italic">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-16 p-10 border border-[#D4AF37]/30 bg-[#0a0a0a] text-center">
          <div className="eyebrow">Ready to talk?</div>
          <h3 className="font-serif text-3xl mt-3">Let's plan your wedding together.</h3>
          <p className="text-white/60 mt-3 max-w-lg mx-auto">A short, honest chat is the easiest first step. We'd love to hear your story.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/contact" className="btn-gold">Say Hello <ArrowRight size={14}/></Link>
            <Link to="/book" className="btn-ghost">Book a 15-min Chat <ArrowRight size={14}/></Link>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <div className="eyebrow mb-6">Keep Reading</div>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map(r => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group" data-testid={`related-${r.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden hover-zoom mb-3">
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#D4AF37]">{r.category}</div>
                  <h4 className="font-serif text-lg mt-1 group-hover:text-[#D4AF37] transition">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
